"use client";

// Usage: <Reveal as="section" id="…" className="…"> … <p className="v2-reveal" style={stagger(1)}> … </Reveal>
// Once about 20 % of the block is visible, Reveal sets data-inview and every .v2-reveal inside fades and rises (order: --i).
// Overrides per element: --reveal-y, --reveal-scale, --reveal-dur, --reveal-ease. No JS or reduced motion: nothing is hidden.
// A block already on screen when the page loads waits for the page's intro, if any: an element carrying
// data-reveal-gate="running" (the homepage hero while it plays its load animation) until it turns to anything else, and
// never more than GATE_MAX_MS after the block mounted (a slow page must not hold its content back, and the intro lasts
// about 2.4 s once it starts). Only while that element is on screen: arriving on an anchor or reloading further down
// (the hero off screen at load), or scrolling it away, reveals at once. Blocks revealed later, on scroll, never wait.

import { createElement, useEffect, useRef, type ElementType, type HTMLAttributes, type ReactNode } from "react";
import "./motion.css";

const GATE_MAX_MS = 4000;

/** Calls `open` once no page intro holds the reveals (see above). Returns a cancel function. */
export function whenGateOpen(open: () => void): () => void {
  const gate = document.querySelector<HTMLElement>('[data-reveal-gate="running"]');
  if (!gate) {
    open();
    return () => {};
  }
  let timer = 0;
  const observer = new MutationObserver(() => {
    if (gate.dataset.revealGate !== "running") finish();
  });
  // Its first call reports whether the intro is on screen at all.
  const onScreen = new IntersectionObserver(([entry]) => {
    if (entry && !entry.isIntersecting) finish();
  });
  const stop = () => {
    observer.disconnect();
    onScreen.disconnect();
    window.clearTimeout(timer);
  };
  const finish = () => {
    stop();
    open();
  };
  observer.observe(gate, { attributes: true, attributeFilter: ["data-reveal-gate"] });
  onScreen.observe(gate);
  timer = window.setTimeout(finish, GATE_MAX_MS);
  return stop;
}

type RevealProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  /** Share of the block that must be visible, capped for blocks taller than the viewport. */
  amount?: number;
  children: ReactNode;
};

export default function Reveal({ as = "div", amount = 0.2, children, ...rest }: RevealProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const show = () => {
      el.dataset.inview = "";
    };
    if (!("IntersectionObserver" in window)) return show();

    // A block taller than twice the viewport can never be 20 % visible at once.
    const ratio = Math.min(amount, (window.innerHeight * 0.5) / Math.max(1, el.offsetHeight));
    // The observer's first call reports the state on mount: visible then means "on screen at load".
    let first = true;
    let cancelGate = () => {};
    const io = new IntersectionObserver(
      (entries) => {
        const atLoad = first;
        first = false;
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= ratio * 0.99)) {
          io.disconnect();
          if (atLoad) cancelGate = whenGateOpen(show);
          else show();
        }
      },
      { threshold: ratio },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelGate();
    };
  }, [amount]);

  return createElement(as, { ...rest, ref, "data-reveal": "" }, children);
}
